import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import { Switch } from '../../../components/ui/Switch';
import { Badge } from '../../../components/ui/Badge';
import { Skeleton } from '../../../components/ui/Skeleton';
import { Shield, Save, RefreshCw, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import {
  useTenantPolicy,
  useUpdateTenantPolicy,
  useResetTenantPolicy,
  TenantPolicy,
} from '../../../hooks/useFinancialSecurity';

export function SecuritySettings() {
  const { data: policyData, isLoading } = useTenantPolicy();
  const updatePolicy = useUpdateTenantPolicy();
  const resetPolicy = useResetTenantPolicy();

  const [formData, setFormData] = useState<TenantPolicy>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form when data loads
  if (policyData?.tenantPolicy && Object.keys(formData).length === 0) {
    setFormData(policyData.tenantPolicy);
  }

  const handleChange = (key: keyof TenantPolicy, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updatePolicy.mutateAsync(formData);
      toast.success('Configurações de segurança atualizadas');
      setHasChanges(false);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao atualizar configurações');
    }
  };

  const handleReset = async () => {
    try {
      await resetPolicy.mutateAsync();
      setFormData({});
      setHasChanges(false);
      toast.success('Configurações restauradas para os padrões da plataforma');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao restaurar configurações');
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const globalPolicy = policyData?.globalPolicy;
  const effectivePolicy = policyData?.effectivePolicy;

  const isCustomized = (key: string) => effectivePolicy?.customizations.includes(key);

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Configurações de Segurança</h1>
            <p className="text-muted-foreground">
              Políticas de segurança financeira para tokenização
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {effectivePolicy?.isCustomized && (
            <Button variant="outline" onClick={handleReset} disabled={resetPolicy.isPending}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Restaurar Padrões
            </Button>
          )}
          <Button onClick={handleSave} disabled={!hasChanges || updatePolicy.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {updatePolicy.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      {/* Status Banner */}
      {effectivePolicy?.isCustomized ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Unlock className="h-5 w-5 text-blue-600" />
          <div>
            <p className="font-medium text-blue-800">Configurações Customizadas</p>
            <p className="text-sm text-blue-600">
              Este tenant possui {effectivePolicy.customizations.length} configuração(ões) customizada(s)
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center gap-3">
          <Lock className="h-5 w-5 text-gray-600" />
          <div>
            <p className="font-medium text-gray-800">Usando Padrões da Plataforma</p>
            <p className="text-sm text-gray-600">
              Todas as configurações seguem as políticas globais
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Coverage Ratio */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Taxa de Cobertura Mínima</CardTitle>
              {isCustomized('coverageRatioThreshold') && (
                <Badge variant="secondary">Customizado</Badge>
              )}
            </div>
            <CardDescription>
              Percentual mínimo de cobertura exigido
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  min={globalPolicy?.minCoverageRatioThreshold ?? 100}
                  max={200}
                  step={1}
                  value={formData.coverageRatioThreshold ?? effectivePolicy?.minCoverageRatioThreshold ?? 100}
                  onChange={(e) => handleChange('coverageRatioThreshold', Number(e.target.value))}
                  className="w-24"
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Mínimo da plataforma: {globalPolicy?.minCoverageRatioThreshold}%
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Custody for Deploy */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Custódia para Deploy</CardTitle>
              {isCustomized('requireCustodyForDeploy') && (
                <Badge variant="secondary">Customizado</Badge>
              )}
            </div>
            <CardDescription>
              Exigir custódia ativa antes do deploy de token
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <Label htmlFor="requireCustodyForDeploy">Exigir Custódia</Label>
              <Switch
                id="requireCustodyForDeploy"
                checked={formData.requireCustodyForDeploy ?? effectivePolicy?.requireCustodyForDeploy ?? true}
                onCheckedChange={(checked) => handleChange('requireCustodyForDeploy', checked)}
                disabled={globalPolicy?.requireCustodyForDeploy && !formData.requireCustodyForDeploy}
              />
            </div>
            {globalPolicy?.requireCustodyForDeploy && (
              <p className="text-xs text-muted-foreground mt-2">
                <Lock className="h-3 w-3 inline mr-1" />
                Obrigatório pela política da plataforma
              </p>
            )}
          </CardContent>
        </Card>

        {/* Custody for Mint */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Custódia para Mint</CardTitle>
              {isCustomized('requireCustodyForMint') && (
                <Badge variant="secondary">Customizado</Badge>
              )}
            </div>
            <CardDescription>
              Exigir custódia ativa e cobertura suficiente para mint
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <Label htmlFor="requireCustodyForMint">Exigir Custódia</Label>
              <Switch
                id="requireCustodyForMint"
                checked={formData.requireCustodyForMint ?? effectivePolicy?.requireCustodyForMint ?? true}
                onCheckedChange={(checked) => handleChange('requireCustodyForMint', checked)}
                disabled={globalPolicy?.requireCustodyForMint && !formData.requireCustodyForMint}
              />
            </div>
            {globalPolicy?.requireCustodyForMint && (
              <p className="text-xs text-muted-foreground mt-2">
                <Lock className="h-3 w-3 inline mr-1" />
                Obrigatório pela política da plataforma
              </p>
            )}
          </CardContent>
        </Card>

        {/* Auto PoR Update */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Atualização Automática de PoR</CardTitle>
              {isCustomized('autoUpdatePoROnSupplyChange') && (
                <Badge variant="secondary">Customizado</Badge>
              )}
            </div>
            <CardDescription>
              Atualizar Proof of Reserve após operações de supply
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <Label htmlFor="autoUpdatePoR">Atualização Automática</Label>
              <Switch
                id="autoUpdatePoR"
                checked={formData.autoUpdatePoROnSupplyChange ?? effectivePolicy?.autoUpdatePoROnSupplyChange ?? true}
                onCheckedChange={(checked) => handleChange('autoUpdatePoROnSupplyChange', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Ledger Integration */}
        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Integração com Ledger para Yields</CardTitle>
              {isCustomized('requireLedgerIntegrationForYield') && (
                <Badge variant="secondary">Customizado</Badge>
              )}
            </div>
            <CardDescription>
              Exigir crédito via ledger para pagamentos de yield
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="requireLedger">Integração Obrigatória</Label>
                <p className="text-xs text-muted-foreground">
                  Garante que pagamentos de yield sejam creditados nas contas virtuais dos holders
                </p>
              </div>
              <Switch
                id="requireLedger"
                checked={formData.requireLedgerIntegrationForYield ?? effectivePolicy?.requireLedgerIntegrationForYield ?? true}
                onCheckedChange={(checked) => handleChange('requireLedgerIntegrationForYield', checked)}
                disabled={globalPolicy?.requireLedgerIntegrationForYield && !formData.requireLedgerIntegrationForYield}
              />
            </div>
            {globalPolicy?.requireLedgerIntegrationForYield && (
              <p className="text-xs text-muted-foreground mt-2">
                <Lock className="h-3 w-3 inline mr-1" />
                Obrigatório pela política da plataforma
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SecuritySettings;
